/**
 * Telecom v2 — Executive Summary scaffolding helper.
 *
 * Generates a board-grade posture-statement draft from objective inputs.
 * Presentation rules (telecom_v2):
 *   - NEVER expose decimal scores, percentages, or NIST function names in the
 *     narrative. Use the four maturity bands only:
 *       Minimal | Adequate | Strong | Extensive
 *   - Reviewer-entered `executive_summary` ALWAYS wins.
 *   - The generated draft is a fallback when reviewer text is missing or thin.
 *   - "Thin" = trimmed length < THIN_THRESHOLD (80 chars).
 */

export const THIN_THRESHOLD = 80;

export type MaturityBand = "Minimal" | "Adequate" | "Strong" | "Extensive";

export type DomainScore = { name: string; score: number; maturity?: string | null };
export type ScaffoldInputs = {
  orgName?: string | null;
  cyberResilienceScore?: number | null; // 0..2 scale (used only to derive band)
  nistScores?: DomainScore[];           // average_score 0..2 (used only to derive bands)
  csvScores?: DomainScore[];            // 0..100 OR 0..2 — auto-detected (used only to derive bands)
  highCriticalCount?: number;           // findings flagged High or Critical
  totalFindings?: number;
};

export function maturityBand(score: number | null | undefined): MaturityBand | null {
  if (score == null || Number.isNaN(score)) return null;
  // Stricter consulting model — parity with calculate_csv_domain_scores RPC.
  if (score >= 1.85) return "Extensive";
  if (score >= 1.50) return "Strong";
  if (score >= 0.50) return "Adequate";
  return "Minimal";
}

/** Detect CSV scale and convert to 0–2 so we can derive a maturity band. */
function csvBand(row: DomainScore): MaturityBand | null {
  const raw = row.score;
  if (raw == null || Number.isNaN(raw)) return null;
  // Heuristic: scores > 2 are 0–100 scale → convert to 0–2.
  const norm = raw > 2 ? (raw / 100) * 2 : raw;
  return maturityBand(norm);
}

function postureSentence(score: number | null | undefined, org: string): string {
  const band = maturityBand(score);
  if (band == null) {
    return `${org} has completed a cybersecurity assessment; an overall cyber resilience rating has not yet been finalised.`;
  }
  const map: Record<MaturityBand, string> = {
    Minimal:   `${org} currently demonstrates a Minimal level of cyber resilience. Foundational controls are not yet consistently implemented and the organisation faces material exposure to disruption, regulatory action, and reputational harm.`,
    Adequate:  `${org} demonstrates an Adequate level of cyber resilience. Core controls are in place but coverage and consistency vary, leaving meaningful residual risk across several capability areas.`,
    Strong:    `${org} demonstrates a Strong level of cyber resilience. Most controls are operating effectively; remaining gaps are concentrated in specific capability areas rather than systemic weaknesses.`,
    Extensive: `${org} demonstrates an Extensive level of cyber resilience. Controls are mature and consistently applied; focus should shift to continuous improvement, threat-led testing, and supply-chain assurance.`,
  };
  return map[band];
}

/** Rank rows by descending score (strongest first), ignoring null scores. */
function rankCsv(rows: DomainScore[], desc: boolean): DomainScore[] {
  return [...rows]
    .filter(r => r.score != null && !Number.isNaN(r.score))
    .sort((a, b) => desc ? (b.score - a.score) : (a.score - b.score));
}

const STRENGTH_PHRASING: Record<string, string> = {
  "Risk Management": "cyber risk identification, treatment, and executive reporting are operating with discipline",
  "Governance": "governance, accountability, and policy oversight are clearly defined and consistently applied",
  "Supply Chain Management": "third-party and supply chain cyber risks are actively assessed and managed",
  "Physical & Environmental Protection": "physical and environmental controls protecting critical infrastructure are consistently enforced",
  "Asset Management": "inventories of systems, data, and network elements are reliable enough to drive risk-based decisions",
  "Identity-Centric Security": "identity controls — including MFA, privileged access, and lifecycle management — are broadly enforced",
  "Security Operations": "monitoring, detection, and incident response are structured and rehearsed",
};

const IMPROVEMENT_PHRASING: Record<string, string> = {
  "Risk Management": "establish a consistent cyber risk identification, treatment, and reporting cycle owned at executive level",
  "Governance": "formalise governance, define accountabilities, and embed cybersecurity oversight into executive and board reporting",
  "Supply Chain Management": "introduce structured vendor due diligence, contractual security expectations, and ongoing monitoring of critical suppliers",
  "Physical & Environmental Protection": "uplift physical access, monitoring, and environmental resilience across critical telecom sites",
  "Asset Management": "build and maintain authoritative inventories of systems, data, and network elements to underpin downstream controls",
  "Identity-Centric Security": "enforce MFA, privileged access management, and joiner-mover-leaver discipline consistently across all critical systems",
  "Security Operations": "stand up centralised logging, tuned detections, and tested incident response playbooks",
};

/**
 * Deterministic Key Strengths text for telecom_v2.
 * Picks up to 3 highest-rated CSV domains in {Strong, Extensive}.
 */
export function buildTelecomKeyStrengths(csv: DomainScore[]): string {
  const eligible = rankCsv(csv, true).filter(r => {
    const b = csvBand(r);
    return b === "Strong" || b === "Extensive";
  });
  if (eligible.length === 0) {
    return "No CSV cybersecurity domains currently rate Strong or Extensive. Strengths will populate once reviewer ratings are recorded.";
  }
  const picks = eligible.slice(0, 3);
  const lines = picks.map((d) => {
    const band = csvBand(d);
    const phrase = STRENGTH_PHRASING[d.name] ?? "controls in this area are operating consistently";
    return `• ${d.name} (${band}): ${phrase}.`;
  });
  return lines.join("\n");
}

/**
 * Deterministic Key Areas for Improvement text for telecom_v2.
 * Picks up to 3 lowest-rated CSV domains in {Minimal, Adequate}.
 */
export function buildTelecomKeyAreas(csv: DomainScore[]): string {
  const eligible = rankCsv(csv, false).filter(r => {
    const b = csvBand(r);
    return b === "Minimal" || b === "Adequate";
  });
  if (eligible.length === 0) {
    return "No CSV cybersecurity domains currently rate Minimal or Adequate. Improvement areas will populate once reviewer ratings are recorded.";
  }
  const picks = eligible.slice(0, 3);
  const lines = picks.map((d) => {
    const band = csvBand(d);
    const phrase = IMPROVEMENT_PHRASING[d.name] ?? "uplift controls in this area to a defensible baseline";
    return `• ${d.name} (${band}): ${phrase}.`;
  });
  return lines.join("\n");
}

/**
 * Build a multi-paragraph executive summary draft (telecom_v2).
 * Uses ONLY maturity bands — never decimal scores, percentages, or
 * NIST function references.
 */
export function buildTelecomExecSummaryDraft(inp: ScaffoldInputs): string {
  const org = inp.orgName?.trim() || "The organisation";
  const lines: string[] = [];

  // Paragraph 1 — overall posture (band only)
  lines.push(postureSentence(inp.cyberResilienceScore ?? null, org));

  // Paragraph 2 — finding concentration (counts only, no scores)
  const hc = inp.highCriticalCount ?? 0;
  const tot = inp.totalFindings ?? 0;
  if (tot > 0) {
    if (hc > 0) {
      lines.push(`The review identified ${tot} finding${tot === 1 ? "" : "s"}, of which ${hc} ${hc === 1 ? "is" : "are"} rated High or Critical and warrant prioritised executive attention.`);
    } else {
      lines.push(`The review identified ${tot} finding${tot === 1 ? "" : "s"}; none are currently rated High or Critical, indicating that residual risks are tractable through planned remediation.`);
    }
  }

  // Paragraph 3 — weakest CSV domains by maturity band
  const csv = (inp.csvScores ?? []).filter(r => r.score != null);
  if (csv.length > 0) {
    const weak = rankCsv(csv, false).filter(r => {
      const b = csvBand(r);
      return b === "Minimal" || b === "Adequate";
    }).slice(0, 2);
    if (weak.length > 0) {
      const names = weak.map(d => `${d.name} (${csvBand(d)})`).join(" and ");
      lines.push(`The cybersecurity domains rated lowest — ${names} — should anchor the remediation roadmap over the next 6–12 months.`);
    }
  }

  // Paragraph 4 — closing executive framing
  lines.push(`This summary is an auto-generated draft based on the maturity ratings recorded in this assessment; the reviewer should refine it with sector context and qualitative observations before publication.`);

  return lines.join("\n\n");
}

/**
 * Decide which text to render in the PDF: reviewer-supplied if substantive, else generated draft.
 */
export function resolveExecSummaryForPdf(reviewerText: string | null | undefined, inp: ScaffoldInputs): {
  text: string;
  source: "reviewer" | "auto";
} {
  const t = (reviewerText ?? "").trim();
  if (t.length >= THIN_THRESHOLD) return { text: t, source: "reviewer" };
  return { text: buildTelecomExecSummaryDraft(inp), source: "auto" };
}
